/**
 * 업종(ksic_category)별 작업 프리셋 — 온보딩 '빠른 선택'과 홈 '작업 추가'의 단일 소스.
 *
 * 안전 지식이 없는 사업주가 자유 입력 없이 체크만으로 작업 목록을 만들 수 있게 한다.
 * work_type 은 formOptions.WORK_TYPE_OPTIONS 어휘와 동일해야 한다(정적 추천·허가서
 * 유형 추론의 키). category: routine=상시, nonroutine=비일상(작업허가서 대상 후보).
 * hints 는 생성 프롬프트 품질 보강용 위험요인 힌트(KRAS 표준모델 기반 일반 명칭).
 * defaultOn 인 항목은 진입 시 미리 체크 — "그냥 다음"만 눌러도 그럴듯한 목록이 되게.
 */
export interface TaskPreset {
  name: string
  work_type: string
  category: 'routine' | 'nonroutine'
  hints?: string[]
  defaultOn?: boolean
}

const OFFICE: TaskPreset[] = [
  { name: '사무실 정리·비품 운반', work_type: '운반·하역', category: 'routine', hints: ['근골격계', '전도'], defaultOn: true },
  { name: '전산·사무기기 점검', work_type: '점검', category: 'routine', hints: ['감전'], defaultOn: true },
  { name: '사다리 이용 수납·정리', work_type: '고소작업', category: 'routine', hints: ['추락'] },
  { name: '차량 운행(외근·배송)', work_type: '운전·조작', category: 'routine', hints: ['교통사고'] },
  { name: '사무실 청소', work_type: '청소', category: 'routine', hints: ['전도', '미끄러짐'] },
  { name: '시설물 보수(전구·설비 교체)', work_type: '정비·보수', category: 'nonroutine', hints: ['감전', '추락'] },
]

export const TASK_PRESETS: Record<string, TaskPreset[]> = {
  manufacturing: [
    { name: '기계·설비 운전', work_type: '운전·조작', category: 'routine', hints: ['끼임', '협착'], defaultOn: true },
    { name: '지게차 운반', work_type: '지게차 작업', category: 'routine', hints: ['충돌', '전도'], defaultOn: true },
    { name: '자재 상하차·적재', work_type: '운반·하역', category: 'routine', hints: ['낙하', '근골격계'], defaultOn: true },
    { name: '중량물 취급(수작업)', work_type: '중량물 취급', category: 'routine', hints: ['근골격계', '깔림'] },
    { name: '용접·절단', work_type: '용접·절단', category: 'routine', hints: ['화상', '화재', '흄 흡입'] },
    { name: '제품 검사·포장', work_type: '점검', category: 'routine', hints: ['베임', '근골격계'] },
    { name: '도장·코팅', work_type: '도장', category: 'routine', hints: ['유기용제', '화재'] },
    { name: '설비 정비·수리', work_type: '정비·보수', category: 'nonroutine', hints: ['끼임', '감전', '기동정지(LOTO)'], defaultOn: true },
    { name: '금형·공구 교체', work_type: '정비·보수', category: 'nonroutine', hints: ['중량물 낙하', '협착'] },
    { name: '설비·배관 청소', work_type: '청소', category: 'nonroutine', hints: ['끼임', '유해물질'] },
  ],
  construction: [
    { name: '비계·고소 작업', work_type: '고소작업', category: 'routine', hints: ['추락'], defaultOn: true },
    { name: '자재 운반·양중', work_type: '크레인 작업', category: 'routine', hints: ['낙하', '충돌'], defaultOn: true },
    { name: '거푸집·형틀 작업', work_type: '설치·시공', category: 'routine', hints: ['붕괴', '낙하'], defaultOn: true },
    { name: '철근·콘크리트 작업', work_type: '설치·시공', category: 'routine', hints: ['근골격계', '찔림'] },
    { name: '굴착·터파기', work_type: '굴착', category: 'routine', hints: ['붕괴', '매몰'] },
    { name: '용접·절단(화기)', work_type: '용접·절단', category: 'routine', hints: ['화재', '화상'] },
    { name: '전기 배선 작업', work_type: '전기작업', category: 'routine', hints: ['감전'] },
    { name: '해체·철거', work_type: '해체·철거', category: 'nonroutine', hints: ['붕괴', '낙하물'], defaultOn: true },
    { name: '밀폐공간 작업(맨홀·피트)', work_type: '밀폐공간 작업', category: 'nonroutine', hints: ['질식', '유해가스'] },
  ],
  chemical: [
    { name: '화학물질 취급·투입', work_type: '화학물질 취급', category: 'routine', hints: ['누출', '화재·폭발', '중독'], defaultOn: true },
    { name: '공정 설비 운전·감시', work_type: '운전·조작', category: 'routine', hints: ['누출', '이상반응'], defaultOn: true },
    { name: '드럼·용기 운반', work_type: '운반·하역', category: 'routine', hints: ['누출', '근골격계'], defaultOn: true },
    { name: '시료 채취·분석', work_type: '점검', category: 'routine', hints: ['흡입', '피부 접촉'] },
    { name: '배관·밸브 점검', work_type: '점검', category: 'routine', hints: ['누출', '고온'] },
    { name: '설비 정비(개방 작업)', work_type: '정비·보수', category: 'nonroutine', hints: ['잔류물질', '기동정지(LOTO)'], defaultOn: true },
    { name: '탱크·반응기 내부 청소', work_type: '밀폐공간 작업', category: 'nonroutine', hints: ['질식', '유해가스'] },
    { name: '화기작업(용접·절단)', work_type: '용접·절단', category: 'nonroutine', hints: ['화재·폭발'] },
  ],
  mining: [
    { name: '채굴·발파 후 작업', work_type: '굴착', category: 'routine', hints: ['붕괴', '낙석'], defaultOn: true },
    { name: '광석 운반(덤프·컨베이어)', work_type: '운반·하역', category: 'routine', hints: ['충돌', '끼임'], defaultOn: true },
    { name: '파쇄·선별 설비 운전', work_type: '운전·조작', category: 'routine', hints: ['끼임', '분진'], defaultOn: true },
    { name: '갱내 점검', work_type: '점검', category: 'routine', hints: ['붕괴', '유해가스'] },
    { name: '설비 정비', work_type: '정비·보수', category: 'nonroutine', hints: ['끼임', '기동정지(LOTO)'] },
    { name: '갱내 밀폐구역 작업', work_type: '밀폐공간 작업', category: 'nonroutine', hints: ['질식'] },
  ],
  electricity_gas: [
    { name: '설비 운전·감시', work_type: '운전·조작', category: 'routine', hints: ['감전', '고온'], defaultOn: true },
    { name: '전기설비 점검', work_type: '전기작업', category: 'routine', hints: ['감전', '아크'], defaultOn: true },
    { name: '배관·밸브 점검', work_type: '점검', category: 'routine', hints: ['누출', '고압'], defaultOn: true },
    { name: '활선 작업', work_type: '전기작업', category: 'nonroutine', hints: ['감전'], defaultOn: true },
    { name: '설비 정비(정전 작업)', work_type: '정비·보수', category: 'nonroutine', hints: ['감전', '기동정지(LOTO)'] },
    { name: '맨홀·지하구 작업', work_type: '밀폐공간 작업', category: 'nonroutine', hints: ['질식', '유해가스'] },
  ],
  environment: [
    { name: '폐기물 수거·운반', work_type: '운반·하역', category: 'routine', hints: ['근골격계', '베임', '감염'], defaultOn: true },
    { name: '선별·파쇄 설비 운전', work_type: '운전·조작', category: 'routine', hints: ['끼임', '분진'], defaultOn: true },
    { name: '지게차·중장비 운행', work_type: '지게차 작업', category: 'routine', hints: ['충돌', '전도'], defaultOn: true },
    { name: '소각·처리 설비 감시', work_type: '점검', category: 'routine', hints: ['고온', '유해가스'] },
    { name: '설비 정비', work_type: '정비·보수', category: 'nonroutine', hints: ['끼임', '기동정지(LOTO)'] },
    { name: '집수조·맨홀 청소', work_type: '밀폐공간 작업', category: 'nonroutine', hints: ['질식', '황화수소'], defaultOn: true },
  ],
  agriculture: [
    { name: '농기계 운전(트랙터 등)', work_type: '운전·조작', category: 'routine', hints: ['전복', '끼임'], defaultOn: true },
    { name: '수확물 운반', work_type: '운반·하역', category: 'routine', hints: ['근골격계'], defaultOn: true },
    { name: '농약·약제 살포', work_type: '화학물질 취급', category: 'routine', hints: ['중독', '흡입'], defaultOn: true },
    { name: '축사 관리·사료 급이', work_type: '점검', category: 'routine', hints: ['가축 접촉', '전도'] },
    { name: '고소 작업(과수·시설)', work_type: '고소작업', category: 'routine', hints: ['추락'] },
    { name: '농기계 정비', work_type: '정비·보수', category: 'nonroutine', hints: ['끼임', '베임'] },
    { name: '저장고·사일로 내부 작업', work_type: '밀폐공간 작업', category: 'nonroutine', hints: ['질식'] },
  ],
  transport: [
    { name: '화물 상하차', work_type: '운반·하역', category: 'routine', hints: ['낙하', '근골격계'], defaultOn: true },
    { name: '지게차 운반', work_type: '지게차 작업', category: 'routine', hints: ['충돌', '전도'], defaultOn: true },
    { name: '차량 운행', work_type: '운전·조작', category: 'routine', hints: ['교통사고', '졸음'], defaultOn: true },
    { name: '적재함 결속·포장', work_type: '중량물 취급', category: 'routine', hints: ['추락', '근골격계'] },
    { name: '랙 적재(창고)', work_type: '고소작업', category: 'routine', hints: ['낙하', '추락'] },
    { name: '차량·장비 정비', work_type: '정비·보수', category: 'nonroutine', hints: ['끼임', '깔림'] },
  ],
  wholesale_retail: [
    { name: '상품 진열·보충', work_type: '운반·하역', category: 'routine', hints: ['근골격계', '전도'], defaultOn: true },
    { name: '창고 입출고', work_type: '운반·하역', category: 'routine', hints: ['낙하', '근골격계'], defaultOn: true },
    { name: '사다리 이용 진열(고소)', work_type: '고소작업', category: 'routine', hints: ['추락'], defaultOn: true },
    { name: '배송 차량 운행', work_type: '운전·조작', category: 'routine', hints: ['교통사고'] },
    { name: '지게차 운반(물류)', work_type: '지게차 작업', category: 'routine', hints: ['충돌'] },
    { name: '매장 청소', work_type: '청소', category: 'routine', hints: ['미끄러짐'] },
    { name: '시설물 보수', work_type: '정비·보수', category: 'nonroutine', hints: ['감전', '추락'] },
  ],
  food_lodging: [
    { name: '조리(화기·기름 취급)', work_type: '운전·조작', category: 'routine', hints: ['화상', '화재'], defaultOn: true },
    { name: '식자재 운반·정리', work_type: '운반·하역', category: 'routine', hints: ['근골격계', '전도'], defaultOn: true },
    { name: '주방 청소(바닥·후드)', work_type: '청소', category: 'routine', hints: ['미끄러짐', '화학세제'], defaultOn: true },
    { name: '칼·절단기 사용', work_type: '운전·조작', category: 'routine', hints: ['베임'] },
    { name: '객실·홀 정비', work_type: '점검', category: 'routine', hints: ['근골격계', '전도'] },
    { name: '후드·덕트 청소(고소)', work_type: '고소작업', category: 'nonroutine', hints: ['추락', '화재'] },
    { name: '주방설비 수리', work_type: '정비·보수', category: 'nonroutine', hints: ['감전', '가스누출'] },
  ],
  healthcare: [
    { name: '환자 이송·부축', work_type: '중량물 취급', category: 'routine', hints: ['근골격계'], defaultOn: true },
    { name: '진료·처치 보조(주사침 등)', work_type: '점검', category: 'routine', hints: ['자상', '감염'], defaultOn: true },
    { name: '세탁물·폐기물 처리', work_type: '운반·하역', category: 'routine', hints: ['감염', '근골격계'], defaultOn: true },
    { name: '소독·약품 취급', work_type: '화학물질 취급', category: 'routine', hints: ['피부 접촉', '흡입'] },
    { name: '병동 청소', work_type: '청소', category: 'routine', hints: ['미끄러짐', '감염'] },
    { name: '의료장비 점검·수리', work_type: '정비·보수', category: 'nonroutine', hints: ['감전'] },
  ],
  it: OFFICE,
  education: [
    ...OFFICE.slice(0, 3),
    { name: '실험·실습 지도', work_type: '화학물질 취급', category: 'routine', hints: ['화상', '흡입'] },
    { name: '체육·야외활동 지도', work_type: '점검', category: 'routine', hints: ['충돌', '전도'] },
    { name: '시설물 보수', work_type: '정비·보수', category: 'nonroutine', hints: ['감전', '추락'] },
  ],
  finance: OFFICE,
  realestate: [
    { name: '건물 순회 점검', work_type: '점검', category: 'routine', hints: ['전도', '추락'], defaultOn: true },
    { name: '전기·기계실 점검', work_type: '전기작업', category: 'routine', hints: ['감전'], defaultOn: true },
    { name: '공용부 청소', work_type: '청소', category: 'routine', hints: ['미끄러짐'], defaultOn: true },
    { name: '조경·외곽 관리', work_type: '점검', category: 'routine', hints: ['베임', '전도'] },
    { name: '옥상·외벽 점검(고소)', work_type: '고소작업', category: 'nonroutine', hints: ['추락'] },
    { name: '설비 보수(승강기·펌프)', work_type: '정비·보수', category: 'nonroutine', hints: ['끼임', '감전'] },
    { name: '저수조·정화조 청소', work_type: '밀폐공간 작업', category: 'nonroutine', hints: ['질식'] },
  ],
  professional: OFFICE,
  facility_support: [
    { name: '건물 청소(바닥·화장실)', work_type: '청소', category: 'routine', hints: ['미끄러짐', '화학세제'], defaultOn: true },
    { name: '외곽·주차장 관리', work_type: '점검', category: 'routine', hints: ['차량 충돌', '전도'], defaultOn: true },
    { name: '경비·순찰', work_type: '점검', category: 'routine', hints: ['전도', '폭력'], defaultOn: true },
    { name: '조경 관리(예초·전지)', work_type: '점검', category: 'routine', hints: ['베임', '날림'] },
    { name: '유리창·외벽 청소(고소)', work_type: '고소작업', category: 'nonroutine', hints: ['추락'], defaultOn: true },
    { name: '시설물 보수', work_type: '정비·보수', category: 'nonroutine', hints: ['감전', '추락'] },
  ],
  arts_sports: [
    { name: '무대·시설 설치', work_type: '설치·시공', category: 'routine', hints: ['낙하', '추락'], defaultOn: true },
    { name: '장비 운반', work_type: '운반·하역', category: 'routine', hints: ['근골격계'], defaultOn: true },
    { name: '시설 점검(체육·오락)', work_type: '점검', category: 'routine', hints: ['끼임', '전도'], defaultOn: true },
    { name: '조명·음향 작업(고소)', work_type: '고소작업', category: 'routine', hints: ['추락', '감전'] },
    { name: '무대 해체', work_type: '해체·철거', category: 'nonroutine', hints: ['낙하'] },
    { name: '시설물 보수', work_type: '정비·보수', category: 'nonroutine', hints: ['감전'] },
  ],
  service: [
    { name: '수리·정비 서비스', work_type: '정비·보수', category: 'routine', hints: ['베임', '감전'], defaultOn: true },
    { name: '고객 응대·매장 관리', work_type: '점검', category: 'routine', hints: ['전도'], defaultOn: true },
    { name: '자재·물품 운반', work_type: '운반·하역', category: 'routine', hints: ['근골격계'], defaultOn: true },
    { name: '차량 운행', work_type: '운전·조작', category: 'routine', hints: ['교통사고'] },
    { name: '매장 청소', work_type: '청소', category: 'routine', hints: ['미끄러짐'] },
    { name: '간판·시설 보수(고소)', work_type: '고소작업', category: 'nonroutine', hints: ['추락'] },
  ],
  other: [
    { name: '물품 운반·정리', work_type: '운반·하역', category: 'routine', hints: ['근골격계'], defaultOn: true },
    { name: '설비·기기 조작', work_type: '운전·조작', category: 'routine', hints: ['끼임'], defaultOn: true },
    { name: '시설 점검', work_type: '점검', category: 'routine', hints: ['전도'], defaultOn: true },
    { name: '청소', work_type: '청소', category: 'routine', hints: ['미끄러짐'] },
    { name: '차량 운행', work_type: '운전·조작', category: 'routine', hints: ['교통사고'] },
    { name: '시설물 보수', work_type: '정비·보수', category: 'nonroutine', hints: ['감전', '추락'] },
  ],
}

export function presetsFor(ksic?: string | null): TaskPreset[] {
  return TASK_PRESETS[ksic ?? ''] ?? TASK_PRESETS.other
}

/**
 * 작업유형 위험 서열 — 백엔드 site_tasks/service.py WORK_TYPE_RISK_ORDER 와 동일해야
 * 한다(단일 서열: 온보딩 무료 생성 기본 선택 = 일일 TBM 쿼터 소진 시 우선 생성 작업).
 */
export const WORK_TYPE_RISK_ORDER: string[] = [
  '밀폐공간 작업', '고소작업', '크레인 작업', '용접·절단', '해체·철거', '굴착',
  '화학물질 취급', '전기작업', '지게차 작업', '중량물 취급', '운반·하역',
  '운전·조작', '정비·보수', '설치·시공', '도장', '방사선 작업', '시운전',
  '청소', '점검',
]

export function workTypeRiskRank(workType?: string | null): number {
  const i = WORK_TYPE_RISK_ORDER.indexOf(workType ?? '')
  return i === -1 ? WORK_TYPE_RISK_ORDER.length : i
}

/**
 * 업종별 '통상 없는 설비' — 백엔드 onboarding/setup.py _INDUSTRY_EXCLUDED 와 동일해야
 * 한다(양쪽 동시 수정). 용도: 작업 상세의 **추천 자동 체크 필터** — 음식점 운반 작업에
 * 지게차·크레인이 기본 체크되면, 사용자가 그냥 넘어갈 때 그 장비가 '있다'고 기록돼
 * 업종 가드(생성 품질)가 무력화된다. 사용자가 손으로 체크하는 것은 막지 않는다(진짜
 * 있는 사업장). ⚠️업종 일반 상식 수준까지만 — 오버피팅 금지.
 */
export const INDUSTRY_EXCLUDED_EQUIPMENT: Record<string, string[]> = {
  food_lodging: ['지게차', '크레인', '체인블록', '호이스트', '컨베이어', '프레스', '용해로', '굴착기', '비계'],
  wholesale_retail: ['크레인', '프레스', '용해로', '굴착기', '비계'],
  it: ['지게차', '크레인', '체인블록', '호이스트', '컨베이어', '프레스', '용해로', '굴착기', '비계'],
  finance: ['지게차', '크레인', '체인블록', '호이스트', '컨베이어', '프레스', '용해로', '굴착기', '비계'],
  professional: ['지게차', '크레인', '체인블록', '호이스트', '컨베이어', '프레스', '용해로', '굴착기', '비계'],
  education: ['지게차', '크레인', '체인블록', '호이스트', '프레스', '용해로', '굴착기'],
  healthcare: ['지게차', '크레인', '체인블록', '호이스트', '컨베이어', '프레스', '용해로', '굴착기', '비계'],
  service: ['크레인', '프레스', '용해로', '굴착기'],
  realestate: ['지게차', '크레인', '체인블록', '호이스트', '프레스', '용해로', '굴착기'],
  arts_sports: ['프레스', '용해로', '굴착기'],
}

/** 업종에 통상 없는 설비를 추천 목록에서 걸러낸다(옵션 라벨 부분 일치). */
export function filterEquipmentForIndustry(items: string[], ksic?: string | null): string[] {
  const excl = INDUSTRY_EXCLUDED_EQUIPMENT[ksic ?? ''] ?? []
  if (!excl.length) return items
  return items.filter(it => !excl.some(e => it.includes(e)))
}
